/**
 * Browser Web Push for the web client (CLIENT-02/03/04).
 *
 * Registration is always a deliberate user action: the permission prompt is
 * never fired on page load. Every terminal state is honest — unsupported
 * browsers are told so, a denied permission is not retried behind the user's
 * back, and the subscription is sent to the authenticated devices endpoint as
 * the platform's own push token.
 */

import { useDayviseClient, useSession } from "./providers.tsx";
import { useDeviceId } from "./deviceIdentity.ts";
import { useScope } from "./hooks.ts";
import { queryKeys } from "./queryKeys.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type WebPushState =
  | { readonly kind: "unsupported"; readonly reason: string }
  | { readonly kind: "denied" }
  | { readonly kind: "unregistered" }
  | { readonly kind: "registered" };

export async function readWebPushState(): Promise<WebPushState> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return { kind: "unsupported", reason: "this browser has no service worker support" };
  }
  if (typeof window === "undefined" || !("PushManager" in window)) {
    return { kind: "unsupported", reason: "this browser has no Web Push support" };
  }
  if (typeof Notification === "undefined") {
    return { kind: "unsupported", reason: "this browser has no notification support" };
  }
  if (Notification.permission === "denied") return { kind: "denied" };
  const registration = await navigator.serviceWorker.getRegistration("/");
  if (!registration) return { kind: "unregistered" };
  const existing = await registration.pushManager.getSubscription();
  if (!existing) return { kind: "unregistered" };
  return { kind: "registered" };
}

function urlBase64ToUint8Array(base64Url: string): Uint8Array {
  const padded = base64Url.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (base64Url.length % 4)) % 4);
  const raw = atob(padded);
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

export interface EnableWebPushInput {
  /** The current user's registered installation in the active workspace. */
  readonly deviceId: string;
  /** Where the app's push service worker is served from. */
  readonly serviceWorkerUrl: string;
  /** PushManager harness injected for testability. */
  readonly registration?: ServiceWorkerRegistration;
  /** Server listing injected for testability (defaults to the SDK). */
  readonly serverKey?: () => Promise<{ webPushPublicKey?: string }>;
}

export interface EnableWebPushResult {
  readonly ok: boolean;
  readonly deviceId?: string;
  readonly state: WebPushState;
  readonly message: string;
}

function subscriptionToToken(subscription: PushSubscriptionJSON): string {
  return JSON.stringify({
    endpoint: subscription.endpoint,
    keys: subscription.keys ?? {},
  });
}

/**
 * Enable push from a click handler. Asks for permission exactly here, registers
 * the service worker, subscribes, and registers the device. The service worker
 * is a same-origin script the deployment serves at `/service-worker.js`.
 */
export async function enableWebPush(input: EnableWebPushInput, client: { devices: { list(): Promise<{ devices: readonly { deviceId: string; platform: string }[]; webPushPublicKey?: string }>; setPushToken(deviceId: string, token: string): Promise<{ device: { deviceId: string } }> } }): Promise<EnableWebPushResult> {
  if (typeof Notification === "undefined" || typeof navigator === "undefined" || !("serviceWorker" in navigator) || !("PushManager" in window)) {
    return { ok: false, state: { kind: "unsupported", reason: "this browser does not support Web Push" }, message: "This browser does not support Web Push; in-app notifications still work." };
  }
  const permission = await Notification.requestPermission();
  if (permission === "denied") {
    return { ok: false, state: { kind: "denied" }, message: "Notifications are blocked for this site. Re-enable them in the browser's site settings to receive push." };
  }
  if (permission !== "granted") {
    return { ok: false, state: { kind: "unregistered" }, message: "Notification permission was not granted. You can enable push again when ready." };
  }
  const registration = input.registration ?? (await navigator.serviceWorker.register(input.serviceWorkerUrl));
  await navigator.serviceWorker.ready;
  const listing = await (input.serverKey ? input.serverKey() : client.devices.list());
  const applicationServerKey = listing.webPushPublicKey;
  if (typeof applicationServerKey !== "string" || applicationServerKey.length === 0) {
    return { ok: false, state: { kind: "unsupported", reason: "the server has no web push key configured" }, message: "Web push is not configured on this deployment yet; in-app notifications still work." };
  }
  const existing = await registration.pushManager.getSubscription();
  const subscription = existing ?? (await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(applicationServerKey) as unknown as BufferSource,
  }));
  const json = subscription.toJSON();
  const registered = await client.devices.setPushToken(input.deviceId, subscriptionToToken(json));
  return { ok: true, deviceId: registered.device.deviceId, state: { kind: "registered" }, message: "Push registered for this browser." };
}

/** Disable only this installation's push delivery in the current workspace. */
export async function disableWebPush(
  client: { devices: { clearPushToken(deviceId: string): Promise<unknown> } },
  deviceId: string,
): Promise<EnableWebPushResult> {
  // The browser subscription belongs to the origin and may serve other signed-in
  // workspaces. Keep it and the device's presence/capability grants intact.
  await client.devices.clearPushToken(deviceId);
  return {
    ok: true,
    deviceId,
    state: { kind: "unregistered" },
    message: "Push disabled for this workspace on this browser.",
  };
}

/**
 * The web client's push settings surface. The enable control is the user
 * gesture that triggers the permission prompt; every outcome (denied,
 * unsupported, server not configured) is stated where the control lives.
 */
export function useWebPushRegistration(serviceWorkerUrl: string) {
  const client = useDayviseClient();
  const scope = useScope();
  const { session } = useSession();
  const deviceId = useDeviceId("web", session.account?.userId ?? null);
  const queryClient = useQueryClient();
  const state = useQuery({
    queryKey: [...queryKeys.devices(scope), "webPushState", deviceId] as const,
    enabled: deviceId !== null,
    queryFn: async (): Promise<WebPushState> => {
      const browser = await readWebPushState();
      if (browser.kind !== "registered") return browser;
      const listing = await client.devices.list();
      return listing.devices.some((device) => device.deviceId === deviceId && device.pushRegistered === true)
        ? browser : { kind: "unregistered" };
    },
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.devices(scope) });
    void queryClient.invalidateQueries({ queryKey: [...queryKeys.devices(scope), "webPushState"] as const });
  };
  const enable = useMutation({
    mutationFn: () => {
      if (deviceId === null) throw new Error("This browser is not registered in the active workspace yet. Reload and try again.");
      return enableWebPush({ serviceWorkerUrl, deviceId }, client);
    },
    onSuccess: refresh,
  });
  const disable = useMutation({
    mutationFn: () => {
      if (deviceId === null) throw new Error("This browser is not registered in the active workspace yet. Reload and try again.");
      return disableWebPush(client, deviceId);
    },
    onSuccess: refresh,
  });
  return { state, enable, disable };
}
